import {
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  where,
  type Firestore,
} from 'firebase/firestore';

export type OfficeDocRefMode = 'unit_short_booking_code' | 'sequence';

const SHORT_BOOKING_CODE_LENGTH = 6;
const SHORT_BOOKING_CODE_MIN = 10 ** (SHORT_BOOKING_CODE_LENGTH - 1);
const SHORT_BOOKING_CODE_RANGE = 9 * SHORT_BOOKING_CODE_MIN;

const randomShortBookingCode = (): string => {
  const raw = new Uint32Array(1);
  if (globalThis.crypto?.getRandomValues) {
    globalThis.crypto.getRandomValues(raw);
  } else {
    raw[0] = Math.floor(Math.random() * Number.MAX_SAFE_INTEGER);
  }
  const value = SHORT_BOOKING_CODE_MIN + (raw[0] % SHORT_BOOKING_CODE_RANGE);
  return String(value);
};

export const normalizeOfficeRefPrefix = (value: string): string =>
  String(value || '')
    .trim()
    .replace(/[^A-Za-z0-9/_-]+/g, '')
    .replace(/^[/_-]+|[/_-]+$/g, '')
    .toUpperCase();

export const buildBookingShortRefNo = (prefix: string, shortBookingCode: string, fallback = ''): string => {
  const normalizedPrefix = normalizeOfficeRefPrefix(prefix);
  const code = String(shortBookingCode || '').trim() || String(fallback || '').trim();
  if (!code) return '';
  return normalizedPrefix ? `${normalizedPrefix}/${code}` : code;
};

export const buildSequenceRefNo = (prefix: string, sequenceNumber: number): string => {
  const normalizedPrefix = normalizeOfficeRefPrefix(prefix);
  const code = String(Math.max(1, Number(sequenceNumber || 1) || 1)).padStart(6, '0');
  return normalizedPrefix ? `${normalizedPrefix}/${code}` : code;
};

export const ensureBookingShortCode = async ({
  db,
  projectId,
  unitId,
  bookingId,
  currentUserEmail,
  existingBooking,
}: {
  db: Firestore;
  projectId: string;
  unitId: string;
  bookingId: string;
  currentUserEmail?: string | null;
  existingBooking?: Record<string, any> | null;
}): Promise<string> => {
  const normalizedProjectId = String(projectId || '').trim();
  const normalizedUnitId = String(unitId || '').trim();
  const normalizedBookingId = String(bookingId || '').trim();
  if (!normalizedProjectId || !normalizedUnitId || !normalizedBookingId) return '';

  const bookingRef = doc(db, 'projects', normalizedProjectId, 'units', normalizedUnitId, 'bookings', normalizedBookingId);
  const source =
    existingBooking && typeof existingBooking === 'object'
      ? existingBooking
      : (() => undefined)();
  const isPlaceholderCode = (value: string) => {
    const normalized = String(value || '').trim();
    return normalized === '123456';
  };

  const currentCode = String(source?.shortBookingCode || '').trim();
  if (currentCode && !isPlaceholderCode(currentCode)) return currentCode;

  const bookingSnap = source ? null : await getDoc(bookingRef);
  const bookingData = source || (bookingSnap?.exists() ? (bookingSnap.data() as Record<string, any>) : null);
  const storedCode = String(bookingData?.shortBookingCode || '').trim();
  if (storedCode && !isPlaceholderCode(storedCode)) return storedCode;

  const candidate = await generateUniqueBookingShortCode({
    db,
    projectId: normalizedProjectId,
    currentPath: bookingRef.path,
  });

  await setDoc(
    bookingRef,
    {
      projectId: normalizedProjectId,
      shortBookingCode: candidate,
      updatedAt: serverTimestamp(),
      updatedBy: currentUserEmail || 'system',
    },
    { merge: true }
  );
  return candidate;
};

export const generateUniqueBookingShortCode = async ({
  db,
  projectId,
  currentPath,
}: {
  db: Firestore;
  projectId: string;
  currentPath?: string;
}): Promise<string> => {
  const normalizedProjectId = String(projectId || '').trim();
  let candidate = '';
  for (let attempt = 0; attempt < 30; attempt += 1) {
    candidate = randomShortBookingCode();
    const existing = await getDocs(
      query(
        collectionGroup(db, 'bookings'),
        where('projectId', '==', normalizedProjectId),
        where('shortBookingCode', '==', candidate),
        limit(1)
      )
    );
    if (existing.empty || existing.docs[0]?.ref?.path === currentPath) return candidate;
  }
  return randomShortBookingCode();
};

export const ensureTemplateSequenceRefNo = async ({
  db,
  templateKey,
  refPrefix,
  existingRefNo,
}: {
  db: Firestore;
  templateKey: string;
  refPrefix: string;
  existingRefNo?: string | null;
}): Promise<string> => {
  const current = String(existingRefNo || '').trim();
  if (current) return current;
  const normalizedKey = String(templateKey || '').trim().replace(/[^\w.-]+/g, '_') || 'office_doc';
  const counterRef = doc(db, 'officeDocumentRefCounters', normalizedKey);
  const nextNumber = await runTransaction(db, async (tx) => {
    const snap = await tx.get(counterRef);
    const lastNumber = Number(snap.data()?.lastNumber || 0) || 0;
    const next = lastNumber + 1;
    tx.set(
      counterRef,
      {
        lastNumber: next,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    return next;
  });
  return buildSequenceRefNo(refPrefix, nextNumber);
};
